import type { ReactNode } from "react";
import { InlineEdit } from "@/components/InlineEdit";
import { BadgeMultiSelect } from "@/components/BadgeMultiSelect";
import { RecordPanel } from "@/components/record/RecordFrame";
import { moneyInputValue } from "@shared/money";
import { CAPITAL_TYPES as SHARED_CAPITAL_TYPES } from "@shared/capitalProviderOptions";
import type { CapitalProfileState } from "./useCapitalProfile";

const CAPITAL_TYPES = [...SHARED_CAPITAL_TYPES];
const COLLATERAL_TYPES = ["Real Estate", "Equipment", "AR/Inventory", "Enterprise Value", "Personal Guarantee", "None/Cash Flow Based"];

const Field = ({ label, wide, children }: { label: string; wide?: boolean; children: ReactNode }) => (
  <div className={wide ? "sm:col-span-2" : undefined}>
    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    {children}
  </div>
);
const list = (v: string) => v.split(",").map((s) => s.trim()).filter(Boolean);

/** What the lender does and on what terms. Every field edits in place; an unset one says Add. */
export function LendingCriteriaPanel({ capital }: { capital: CapitalProfileState }) {
  const { profile, saveProfile, saveMoney } = capital;
  return (
    <RecordPanel title="Lending criteria">
      <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        <Field label="Capital types">
          <BadgeMultiSelect selected={profile?.capitalTypes || null} options={CAPITAL_TYPES} placeholder="Select capital types" addLabel="Add" onSave={(v) => saveProfile({ capitalTypes: v })} />
        </Field>
        <Field label="Collateral types">
          <BadgeMultiSelect selected={profile?.collateralTypes || null} options={COLLATERAL_TYPES} placeholder="Select collateral types" addLabel="Add" onSave={(v) => saveProfile({ collateralTypes: v })} />
        </Field>
        <Field label="Min deal size">
          <InlineEdit label="Min deal size" value={moneyInputValue(profile?.minDealSize) || null} placeholder="e.g. $5M" addLabel="Add" onSave={saveMoney("minDealSize")} />
        </Field>
        <Field label="Max deal size">
          <InlineEdit label="Max deal size" value={moneyInputValue(profile?.maxDealSize) || null} placeholder="e.g. $50M" addLabel="Add" onSave={saveMoney("maxDealSize")} />
        </Field>
        <Field label="Sweet spot">
          <InlineEdit label="Sweet spot" value={profile?.sweetSpot || null} placeholder="e.g. $10-25M" addLabel="Add" onSave={(v) => saveProfile({ sweetSpot: v })} />
        </Field>
        <Field label="Min EBITDA">
          <InlineEdit label="Min EBITDA" value={moneyInputValue(profile?.minEbitda) || null} placeholder="e.g. $2M" addLabel="Add" onSave={saveMoney("minEbitda")} />
        </Field>
        <Field label="Industry focus">
          <InlineEdit label="Industry focus" value={profile?.industryFocus?.join(", ") || null} placeholder="e.g. Healthcare, Manufacturing" addLabel="Add" onSave={(v) => saveProfile({ industryFocus: list(v) })} />
        </Field>
        <Field label="Industry exclusions">
          <InlineEdit label="Industry exclusions" value={profile?.industryExclusions?.join(", ") || null} placeholder="e.g. Cannabis, Crypto" addLabel="Add" onSave={(v) => saveProfile({ industryExclusions: list(v) })} />
        </Field>
        <Field label="Geographic focus" wide>
          <InlineEdit label="Geographic focus" value={profile?.geographicFocus || null} placeholder="e.g. National, Texas/Oklahoma" addLabel="Add" onSave={(v) => saveProfile({ geographicFocus: v })} />
        </Field>
      </div>
      <div className="mt-3 grid gap-x-6 gap-y-3 border-t pt-3 sm:grid-cols-2">
        <Field label="Typical rate">
          <InlineEdit label="Typical rate" value={profile?.typicalRate || null} placeholder="e.g. SOFR + 400-600bps" addLabel="Add" onSave={(v) => saveProfile({ typicalRate: v })} />
        </Field>
        <Field label="Typical leverage">
          <InlineEdit label="Typical leverage" value={profile?.typicalLeverage || null} placeholder="e.g. 3-4x EBITDA" addLabel="Add" onSave={(v) => saveProfile({ typicalLeverage: v })} />
        </Field>
        <Field label="Speed to close">
          <InlineEdit label="Speed to close" value={profile?.speedToClose || null} placeholder="e.g. 30-45 days" addLabel="Add" onSave={(v) => saveProfile({ speedToClose: v })} />
        </Field>
        <Field label="Structure notes" wide>
          <InlineEdit label="Structure notes" value={profile?.structureNotes || null} placeholder="Notes on typical deal structure" type="textarea" addLabel="Add" onSave={(v) => saveProfile({ structureNotes: v })} />
        </Field>
      </div>
    </RecordPanel>
  );
}
